import {
  KatButton,
  KatIcon,
  KatLink,
  KatPageHeader,
  KatPageHeaderUtilityActions,
  KatPageHeaderUtilityLinks,
  KatSpinner,
} from "@amzn/katal-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  type NestUseCase,
  type UploadProgress,
  UploadStatus,
} from "src/api/interfaces/nest";
import { NestTable } from "./components/NestTable";
import { NestUploadCard } from "./components/Upload/NestUploadCard";
import { NestUploadProgressFooter } from "./components/Upload/NestUploadProgressFooter";
import "./index.css";
import AICLayout from "src/components/AICLayout/AICLayout";
import NestHistory from "./components/History";
import { NestHistoryDetail } from "./components/History/NestHistoryDetail";
import { useNestContent } from "./hooks/useNestContent";
import { useDocumentTypePreference } from "./hooks/useDocumentTypePreference";
import { NestPartnerAlert } from "./components/NestPartnerAlert";
import { NestExpectationsPanel } from "./components/NestExpectationsPanel";
import { NestDisclaimerPanel } from "./components/NestDisclaimerPanel";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useAlertContext } from "src/components/Alert/AlertContext";
import { alertHandler } from "src/components/Alert/handlers";
import { useNest } from "src/components/Contexts/NestContext";
import { isPayeeCentral } from "src/api/config";
import client from "src/api/client";
import { setRealmBasedOnCountrySelection } from "src/api/interfaces/realm";
import { LogLevel, log } from "src/logger";
import { isPageAllowed } from "src/api/interfaces/pageUtils";
import { getUser } from "src/api/interfaces/user";

export default function Nest() {
  return (
    <div className="nest-page">
      <AICLayout alertClassName="nest-alert-box" isNest>
        <NestSessionGate />
      </AICLayout>
    </div>
  );
}

function NestSessionGate() {
  const { t } = useTranslation();
  const { setCountrySelection } = useCountrySelection();
  const [isSessionReady, setIsSessionReady] = useState(!isPayeeCentral());
  const [sessionError, setSessionError] = useState(false);

  useEffect(() => {
    if (isPayeeCentral()) {
      client
        .getNestSessionInfo()
        .then(({ countryCode }) => {
          const country = { countryCode };
          setCountrySelection({
            availableCountries: [country],
            selectedCountry: country,
          });
          setRealmBasedOnCountrySelection(country);
          setIsSessionReady(true);
        })
        .catch((error) => {
          log({
            level: LogLevel.ERROR,
            message: "Failed to fetch Nest session info",
            operationNamespace: "NestSessionGate",
            exception: error?.message,
          });
          setSessionError(true);
        });
    }
  }, [setCountrySelection]);

  if (sessionError) {
    return <p>{t("aic_nest_session_error")}</p>;
  }

  if (!isSessionReady) {
    return <KatSpinner />;
  }

  return <NestContent />;
}

function NestContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { getDocumentType } = useDocumentTypePreference();
  const { selectedCountry } = useCountrySelection();
  const { partnerCode } = useNest();
  const { alerts, setAlerts } = useAlertContext();
  const {
    useCases,
    isLoading,
    selectedUploadUseCase,
    uploadFiles,
    uploadUseCaseId,
    uploadDocumentTypeId,
    useCaseHistory,
    selectedUploadTest,
    isUploading,
    handleUseCaseUploadRequested,
    handleNewTestRequested,
    handleUploadStarted,
    handleUploadFinished,
    handleStatusClick,
    handleHistoryOpened,
    handleClose,
  } = useNestContent();

  useEffect(() => {
    if (selectedCountry) {
      handleClose();
    }
  }, [selectedCountry, partnerCode, handleClose]);

  const onStatusClick = useCallback(
    async (useCase: NestUseCase) => {
      try {
        await handleStatusClick(useCase);
      } catch {
        alertHandler.createNewAlertEntry({
          fade: true,
          text: t("aic_nest_status_click_error"),
          isSuccess: false,
          alerts,
          setAlerts,
        });
      }
    },
    [handleStatusClick, alerts, setAlerts, t]
  );

  const onUploadFinished = useCallback(
    (results: UploadProgress[]) => {
      const failedCount = results.filter(
        (u) => u.status === UploadStatus.ERROR
      ).length;
      const isSuccess = failedCount === 0;
      const count = isSuccess ? results.length - failedCount : failedCount;

      alertHandler.createNewAlertEntry({
        fade: true,
        isSuccess,
        text: t(
          isSuccess ? "aic_nest_upload_success" : "aic_nest_upload_failed",
          {
            count,
            fileName: results[0]?.fileName,
          }
        ),
        alerts,
        setAlerts,
      });

      handleUploadFinished(results);
    },
    [handleUploadFinished, alerts, setAlerts, t]
  );

  let sidePanel: React.ReactNode = null;
  if (selectedUploadTest) {
    sidePanel = (
      <NestHistoryDetail
        test={selectedUploadTest}
        onBack={() => handleClose()}
        onNewTest={handleNewTestRequested}
        onClose={() => handleClose()}
        showBack={false}
      />
    );
  } else if (useCaseHistory) {
    sidePanel = (
      <NestHistory
        useCase={useCaseHistory}
        onNewTest={handleNewTestRequested}
        onClose={() => handleClose()}
      />
    );
  } else if (selectedUploadUseCase) {
    sidePanel = (
      <NestExpectationsPanel
        useCase={selectedUploadUseCase}
        documentTypeName={getDocumentType(selectedUploadUseCase).name}
        onClose={() => handleClose()}
      />
    );
  } else {
    sidePanel = <NestDisclaimerPanel />;
  }

  const user = getUser();
  return (
    <div className="nest-container">
      <NestPartnerAlert />
      <KatPageHeader
        className="nest-header"
        header={t("aic_nest_home_page_title")}
        subheader={t("aic_nest_home_page_description")}
      >
        {user == "VENDORCENTRAL" && isPageAllowed("AIC") && (
          <>
            <KatPageHeaderUtilityLinks>
              <KatLink href={`/inbound-central${location.search}`}>
                {t("aic_nest_go", { isNest: true })}
              </KatLink>
            </KatPageHeaderUtilityLinks>
            <KatPageHeaderUtilityActions>
              <KatButton
                onClick={() =>
                  navigate({
                    pathname: "/inbound-central",
                    search: location.search,
                  })
                }
              >
                <KatIcon name="chevron-left" size="small"></KatIcon>
              </KatButton>
            </KatPageHeaderUtilityActions>
          </>
        )}
      </KatPageHeader>
      <div className="nest-content">
        <div className="nest-main-panel">
          {isLoading ? (
            <KatSpinner />
          ) : (
            <NestTable
              data={useCases}
              onUpload={handleUseCaseUploadRequested}
              onHistory={handleHistoryOpened}
              onStatusClick={onStatusClick}
              isUploading={isUploading}
            />
          )}
          {selectedUploadUseCase && (
            <NestUploadCard
              useCase={selectedUploadUseCase}
              documentTypeName={getDocumentType(selectedUploadUseCase).name}
              onFilesSelected={(files) =>
                handleUploadStarted(files, selectedUploadUseCase.id)
              }
            />
          )}
        </div>
        <div
          className={`nest-side-panel overflow-x-hidden min-w-0 p-2${useCaseHistory || selectedUploadTest ? " nest-history-panel" : ""}`}
        >
          {sidePanel}
        </div>
      </div>
      {uploadFiles.length > 0 && (
        <NestUploadProgressFooter
          files={uploadFiles}
          useCaseId={uploadUseCaseId}
          documentTypeId={uploadDocumentTypeId}
          onComplete={onUploadFinished}
        />
      )}
    </div>
  );
}
